import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { useOverlayState } from "./hooks/useOverlayState";
import { useCanvasRendering } from "./hooks/useCanvasRendering";
import { useOverlayInteractions } from "./hooks/useOverlayInteractions";
import { TextOverlay, LogoOverlay, SlateOverlay } from "./types/overlayTypes";
import {
  calculateTextDimensions,
  createTextContext,
} from "./utils/textCalculations";
import styles from "./media-card.module.scss";

interface CanvasOverlayRendererProps {
  showCanvasOverlay: boolean;
  textOverlays: TextOverlay[];
  logoOverlays: LogoOverlay[];
  slateOverlays?: SlateOverlay[]; // New prop
  onTextOverlaysChange: (textOverlays: TextOverlay[]) => void;
  onLogoOverlaysChange: (logoOverlays: LogoOverlay[]) => void;
  onSlateOverlaysChange?: (slateOverlays: SlateOverlay[]) => void; // New callback
  onTextOverlaySelect?: (textOverlayId: string) => void;
  onLogoOverlaySelect?: (logoOverlayId: string) => void;
  selectedTextOverlayId?: string;
  selectedLogoOverlayId?: string;
  onCloseLogoSliders?: () => void;
  externalShowTextModal?: boolean;
  externalShowLogoModal?: boolean;
  externalTextEditingState?: any;
  externalLogoEditingState?: any;
  onOverlayChange?: (overlayData: any) => void;
  onHighResCapture?: (canvas: HTMLCanvasElement) => void;
  videoDimensions?: { width: number; height: number }; // New prop for video dimensions
  contentNonEditable?: boolean; // New prop to make content non-editable
  isPreset?: boolean;
  readOnlyMode?: boolean;
  maintainAspectRatio?: boolean;
}

export const CanvasOverlayRenderer: React.FC<CanvasOverlayRendererProps> = ({
  showCanvasOverlay,
  textOverlays,
  logoOverlays,
  slateOverlays = [], // Default to empty array
  onTextOverlaysChange,
  onLogoOverlaysChange,
  onSlateOverlaysChange,
  onTextOverlaySelect,
  onLogoOverlaySelect,
  selectedTextOverlayId,
  selectedLogoOverlayId,
  onCloseLogoSliders,
  externalShowTextModal = false,
  externalShowLogoModal = false,
  externalTextEditingState,
  externalLogoEditingState,
  onOverlayChange,
  onHighResCapture,
  videoDimensions,
  contentNonEditable = false,
  isPreset = false,
  readOnlyMode = false,
  maintainAspectRatio = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoImagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const onHighResCaptureRef = useRef(onHighResCapture);
  const inlineInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    onHighResCaptureRef.current = onHighResCapture;
  }, [onHighResCapture]);

  const {
    dragState,
    setDragState,
    resizeState,
    setResizeState,
    textEditingState,
    setTextEditingState,
    inlineTextEditingState,
    setInlineTextEditingState,
    textResizeState,
    setTextResizeState,
    textRotationState,
    setTextRotationState,
    showTextModal,
    setShowTextModal,
    logoEditingState,
    setLogoEditingState,
    showLogoModal,
    setShowLogoModal,
  } = useOverlayState();

  useEffect(() => {
    if (inlineTextEditingState.isEditing && inlineInputRef.current) {
      const focusElement = () => {
        if (inlineInputRef.current) {
          inlineInputRef.current.focus({ preventScroll: true });
          const length = inlineInputRef.current.value.length;
          inlineInputRef.current.setSelectionRange(length, length);
          // Reset scroll position to prevent text from appearing scrolled
          inlineInputRef.current.scrollTop = 0;
          inlineInputRef.current.scrollLeft = 0;
        }
      };
      focusElement();
      setTimeout(focusElement, 10);
    }
  }, [inlineTextEditingState.isEditing, inlineTextEditingState.textOverlayId]);

  useEffect(() => {
    if (
      inlineTextEditingState.isEditing &&
      inlineTextEditingState.textOverlayId
    ) {
      const currentTextOverlay = textOverlays.find(
        (overlay) => overlay.id === inlineTextEditingState.textOverlayId
      );
      if (currentTextOverlay) {
        setInlineTextEditingState((prev) => ({
          ...prev,
          position: currentTextOverlay.position,
          fontSize: currentTextOverlay.fontSize,
          text:
            prev.text !== undefined && prev.text !== ""
              ? prev.text
              : currentTextOverlay.text || "",
          padding: currentTextOverlay.padding || 0,
          rotation: currentTextOverlay.rotation || 0,
          isBold: currentTextOverlay.isBold || false,
          isItalic: currentTextOverlay.isItalic || false,
          isUnderline: currentTextOverlay.isUnderline || false,
          fontFamily: currentTextOverlay.fontFamily || "Arial",
        }));
      } else {
        setInlineTextEditingState(resetInlineTextEditingState());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    textOverlays,
    inlineTextEditingState.isEditing,
    inlineTextEditingState.textOverlayId,
  ]);

  const constrainTextOverlayPosition = (
    overlay: TextOverlay
  ): { x: number; y: number } => {
    if (!canvasRef.current || !videoDimensions) {
      return overlay.position;
    }

    const canvas = canvasRef.current;
    const canvasRect = canvas.getBoundingClientRect();
    const videoWidth = videoDimensions.width;
    const videoHeight = videoDimensions.height;

    const canvasAspectRatio = canvasRect.width / canvasRect.height;
    const videoAspectRatio = videoWidth / videoHeight;

    let scaleX = 1;
    let scaleY = 1;

    if (canvasAspectRatio > videoAspectRatio) {
      scaleY = canvasRect.height / videoHeight;
      scaleX = scaleY;
    } else {
      scaleX = canvasRect.width / videoWidth;
      scaleY = scaleX;
    }

    const maxWidth = videoWidth * Math.min(scaleX, scaleY);
    const padding = overlay.padding || 0;
    const textForCalculation = overlay.text || "New Text";

    const tempCtx = createTextContext(overlay);
    if (!tempCtx) {
      return overlay.position;
    }

    const { textWidth, textHeight } = calculateTextDimensions(
      tempCtx,
      textForCalculation,
      overlay.fontSize,
      padding,
      maxWidth
    );
    const paddedWidth = textWidth + padding * 2;
    const paddedHeight = textHeight + padding * 2;
    const rotation = overlay.rotation || 0;
    const rotationRad = (rotation * Math.PI) / 180;

    const cosRot = Math.abs(Math.cos(rotationRad));
    const sinRot = Math.abs(Math.sin(rotationRad));
    const rotatedWidth = paddedWidth * cosRot + paddedHeight * sinRot;
    const rotatedHeight = paddedWidth * sinRot + paddedHeight * cosRot;

    const backgroundPaddingX = 16;
    const backgroundPaddingY = 8;
    const totalRotatedWidth = rotatedWidth + backgroundPaddingX;
    const totalRotatedHeight = rotatedHeight + backgroundPaddingY;

    const videoAreaWidth = videoWidth * scaleX;
    const videoAreaHeight = videoHeight * scaleY;
    const textWidthPercent = (totalRotatedWidth / videoAreaWidth) * 100;
    const textHeightPercent = (totalRotatedHeight / videoAreaHeight) * 100;

    const minX = textWidthPercent / 2 + 0.5;
    const maxX = 100 - textWidthPercent / 2 - 0.5;
    const minY = textHeightPercent / 2 + 0.5;
    const maxY = 100 - textHeightPercent / 2 - 0.5;

    const constrainedX = Math.max(minX, Math.min(maxX, overlay.position.x));
    const constrainedY = Math.max(minY, Math.min(maxY, overlay.position.y));

    return { x: constrainedX, y: constrainedY };
  };

  // Update functions
  const updateTextOverlay = (
    id: string,
    updates: Partial<TextOverlay>,
    constrainPosition = false
  ) => {
    const currentOverlay = textOverlays.find((overlay) => overlay.id === id);
    if (!currentOverlay) {
      return;
    }

    const updatedOverlay = { ...currentOverlay, ...updates };

    if (
      updates.text !== undefined ||
      updates.fontSize !== undefined ||
      updates.padding !== undefined ||
      updates.rotation !== undefined
    ) {
      const constrainedPosition = constrainTextOverlayPosition(updatedOverlay);
      if (
        Math.abs(constrainedPosition.x - updatedOverlay.position.x) > 0.01 ||
        Math.abs(constrainedPosition.y - updatedOverlay.position.y) > 0.01
      ) {
        updatedOverlay.position = constrainedPosition;
        if (
          inlineTextEditingState.isEditing &&
          inlineTextEditingState.textOverlayId === id
        ) {
          const shouldUpdatePosition =
            !updates.text || updates.text === currentOverlay.text;
          if (shouldUpdatePosition) {
            setInlineTextEditingState((prev) => ({
              ...prev,
              position: constrainedPosition,
            }));
          }
        }
      }
    }

    const updatedTextOverlays = textOverlays.map((overlay) =>
      overlay.id === id ? updatedOverlay : overlay
    );
    onTextOverlaysChange(updatedTextOverlays);
    if (showTextModal && textEditingState.textOverlayId === id) {
      setTextEditingState((prev) => ({
        ...prev,
        ...updates,
      }));
    }
  };

  const updateLogoOverlay = (id: string, updates: Partial<LogoOverlay>) => {
    const updatedLogoOverlays = logoOverlays.map((logo) =>
      logo.id === id ? { ...logo, ...updates } : logo
    );
    onLogoOverlaysChange(updatedLogoOverlays);
  };

  const updateSlateOverlay = (id: string, updates: Partial<SlateOverlay>) => {
    const updatedSlateOverlays = slateOverlays.map((slate) =>
      slate.id === id ? { ...slate, ...updates } : slate
    );
    onSlateOverlaysChange?.(updatedSlateOverlays);
    onOverlayChange?.({
      textOverlays,
      logoOverlays,
      slateOverlays: updatedSlateOverlays,
    });
  };

  const resetInlineTextEditingState = () => ({
    isEditing: false,
    textOverlayId: undefined,
    text: "",
    position: { x: 0, y: 0 },
    fontSize: 24,
    fontColor: "#ffffff",
    backgroundColor: "#000000",
    isBold: false,
    isItalic: false,
    isUnderline: false,
    padding: 0,
    rotation: 0,
    opacity: 1.0,
    fontFamily: "Arial",
  });

  const handleTextEditSave = () => {
    setTextEditingState(resetInlineTextEditingState());
    setShowTextModal(false);
  };

  const handleTextEditCancel = () => {
    setTextEditingState(resetInlineTextEditingState());
    setShowTextModal(false);
  };

  const { renderCanvas, captureHighResCanvas } = useCanvasRendering(
    canvasRef,
    textOverlays,
    logoOverlays,
    slateOverlays, // Pass slates
    dragState,
    resizeState,
    textEditingState,
    textResizeState,
    textRotationState,
    showCanvasOverlay,
    logoImagesRef,
    videoDimensions, // Pass video dimensions
    readOnlyMode, // Pass readOnlyMode
    selectedTextOverlayId, // Pass selected text overlay ID
    selectedLogoOverlayId // Pass selected logo overlay ID
  );

  const triggerCanvasCapture = useCallback(async () => {
    if (onHighResCaptureRef.current && captureHighResCanvas) {
      try {
        const highResCanvas = await captureHighResCanvas();
        if (highResCanvas) {
          onHighResCaptureRef.current(highResCanvas);
        }
      } catch (error) {
        console.error("Error during canvas capture:", error);
      }
    }
  }, [captureHighResCanvas]);

  const textOverlaysKey = useMemo(
    () =>
      textOverlays
        .map(
          (o) =>
            `${o.id}-${o.visible}-${o.text}-${o.fontSize}-${o.fontColor}-${
              o.backgroundColor
            }-${o.position.x}-${o.position.y}-${o.isBold}-${o.isItalic}-${
              o.isUnderline
            }-${o.padding}-${o.rotation || 0}-${o.opacity || 1}`
        )
        .join("|"),
    [textOverlays]
  );

  const logoOverlaysKey = useMemo(
    () =>
      logoOverlays
        .map(
          (o) =>
            `${o.id}-${o.visible}-${o.url}-${o.position.x}-${o.position.y}-${
              o.size.width
            }-${o.size.height}-${o.opacity || 1}-${o.rotation || 0}`
        )
        .join("|"),
    [logoOverlays]
  );

  useEffect(() => {
    const hasVisibleContent =
      showCanvasOverlay ||
      textOverlays.some((overlay) => overlay.visible && overlay.text) ||
      logoOverlays.some((overlay) => overlay.visible && overlay.url) ||
      // watermarkOverlays.some((overlay) => overlay.visible && overlay.url) ||
      slateOverlays.some((overlay) => overlay.visible && overlay.url);

    if (hasVisibleContent && onHighResCaptureRef.current) {
      setTimeout(async () => {
        try {
          triggerCanvasCapture();
        } catch (error) {
          console.error("Error in triggerCanvasCapture:", error);
        }
      }, 200);
    }
  }, [textOverlaysKey, logoOverlaysKey, showCanvasOverlay]);

  const { handleDoubleClick, handleMouseDown, handleCursorMove } =
    useOverlayInteractions(
      canvasRef,
      textOverlays,
      logoOverlays,
      slateOverlays,
      dragState,
      resizeState,
      textResizeState,
      textRotationState,
      inlineTextEditingState,
      showCanvasOverlay,
      showTextModal,
      setDragState,
      setResizeState,
      setTextResizeState,
      setTextRotationState,
      setTextEditingState,
      setInlineTextEditingState,
      setLogoEditingState,
      setShowTextModal,
      setShowLogoModal,
      updateTextOverlay,
      updateLogoOverlay,
      updateSlateOverlay,
      onTextOverlaySelect,
      onLogoOverlaySelect,
      handleTextEditCancel,
      videoDimensions,
      renderCanvas,
      contentNonEditable,
      readOnlyMode,
      maintainAspectRatio
    );

  // Keyboard event handlers for modal editing only
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (textEditingState.isEditing) {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleTextEditSave();
        } else if (e.key === "Escape") {
          e.preventDefault();
          handleTextEditCancel();
        }
      }
    };

    if (textEditingState.isEditing) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [textEditingState.isEditing]);

  const currentTextOverlayForEditing = inlineTextEditingState.isEditing
    ? textOverlays.find(
        (overlay) => overlay.id === inlineTextEditingState.textOverlayId
      )
    : null;

  const getTextareaStyle = (
    position: { x: number; y: number },
    fontSize: number,
    text: string,
    overlay?: TextOverlay
  ) => {
    const validPosition = {
      x: isNaN(position.x) || position.x === undefined ? 50 : position.x,
      y: isNaN(position.y) || position.y === undefined ? 50 : position.y,
    };

    if (!canvasRef.current || !videoDimensions) {
      return {
        top: `${validPosition.y}%`,
        left: `${validPosition.x}%`,
        fontSize: `${fontSize}px`,
        width: "auto",
        height: "auto",
      };
    }

    const canvas = canvasRef.current;
    const canvasRect = canvas.getBoundingClientRect();
    const videoWidth = videoDimensions.width;
    const videoHeight = videoDimensions.height;

    const canvasAspectRatio = canvasRect.width / canvasRect.height;
    const videoAspectRatio = videoWidth / videoHeight;

    let scaleX = 1;
    let scaleY = 1;
    let offsetX = 0;
    let offsetY = 0;

    if (canvasAspectRatio > videoAspectRatio) {
      scaleY = canvasRect.height / videoHeight;
      scaleX = scaleY;
      offsetX = (canvasRect.width - videoWidth * scaleX) / 2;
    } else {
      scaleX = canvasRect.width / videoWidth;
      scaleY = scaleX;
      offsetY = (canvasRect.height - videoHeight * scaleY) / 2;
    }
    const textX = validPosition.x;
    const textY = validPosition.y;

    const videoX = (textX / 100) * videoWidth;
    const videoY = (textY / 100) * videoHeight;
    const textCanvasPosition = {
      x: isNaN(videoX * scaleX + offsetX) ? 0 : videoX * scaleX + offsetX,
      y: isNaN(videoY * scaleY + offsetY) ? 0 : videoY * scaleY + offsetY,
    };
    const padding =
      (overlay?.padding || inlineTextEditingState.padding || 0) *
      Math.min(scaleX, scaleY);
    const maxWidth = videoWidth * Math.min(scaleX, scaleY);

    const textForCalculation = text || "New Text";

    // Create a temporary overlay object for context creation
    const tempOverlay: TextOverlay = {
      id: overlay?.id || "",
      text: textForCalculation,
      fontSize,
      fontColor: overlay?.fontColor || "#ffffff",
      backgroundColor: overlay?.backgroundColor || "#000000",
      position: { x: 0, y: 0 },
      visible: true,
      isBold: overlay?.isBold || inlineTextEditingState.isBold || false,
      isItalic: overlay?.isItalic || inlineTextEditingState.isItalic || false,
      isUnderline:
        overlay?.isUnderline || inlineTextEditingState.isUnderline || false,
      padding: overlay?.padding || inlineTextEditingState.padding || 0,
      fontFamily:
        overlay?.fontFamily || inlineTextEditingState.fontFamily || "Arial",
    };

    const tempCtx = createTextContext(tempOverlay, fontSize);
    if (!tempCtx) {
      return {
        top: `${validPosition.y}%`,
        left: `${validPosition.x}%`,
        fontSize: `${fontSize}px`,
        width: "auto",
        height: "auto",
      };
    }

    const {
      textWidth: rawTextWidth,
      textHeight: rawTextHeight,
      lineHeight,
    } = calculateTextDimensions(
      tempCtx,
      textForCalculation,
      fontSize,
      padding,
      maxWidth
    );

    // Ensure minimum dimensions
    const textHeight = Math.max(rawTextHeight, lineHeight);
    const textWidth = Math.max(
      rawTextWidth,
      tempCtx.measureText("New Text").width
    );
    const paddedWidth = textWidth + padding * 2;
    const paddedHeight = textHeight + padding * 2;

    const backgroundWidth = paddedWidth + 16;
    const backgroundHeight = paddedHeight + 8;
    const leftPercent =
      canvasRect.width > 0
        ? (textCanvasPosition.x / canvasRect.width) * 100
        : validPosition.x;
    const topPercent =
      canvasRect.height > 0
        ? (textCanvasPosition.y / canvasRect.height) * 100
        : validPosition.y;
    const validLeftPercent = isNaN(leftPercent) ? validPosition.x : leftPercent;
    const validTopPercent = isNaN(topPercent) ? validPosition.y : topPercent;

    return {
      top: `${validTopPercent}%`,
      left: `${validLeftPercent}%`,
      fontSize: `${fontSize}px`,
      width: `${backgroundWidth}px`,
      height: `${backgroundHeight}px`,
      transform: "translate(-50%, -50%)", // Center the textarea like canvas rendering
    };
  };
  const positionKey = currentTextOverlayForEditing
    ? `${currentTextOverlayForEditing.position.x},${currentTextOverlayForEditing.position.y}`
    : null;
  const textareaStyle = useMemo(() => {
    if (inlineTextEditingState.isEditing && currentTextOverlayForEditing) {
      return getTextareaStyle(
        currentTextOverlayForEditing.position,
        currentTextOverlayForEditing.fontSize,
        currentTextOverlayForEditing.text || inlineTextEditingState.text,
        currentTextOverlayForEditing
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inlineTextEditingState.isEditing,
    inlineTextEditingState.textOverlayId,
    positionKey,
    currentTextOverlayForEditing?.fontSize,
    currentTextOverlayForEditing?.text,
    currentTextOverlayForEditing?.rotation,
    inlineTextEditingState.text,
    videoDimensions?.width,
    videoDimensions?.height,
    textOverlays,
  ]);

  const hasContent =
    showCanvasOverlay ||
    textOverlays.some((overlay) => overlay.visible) ||
    logoOverlays.some((overlay) => overlay.visible && overlay.url);

  if (!hasContent) {
    return null;
  }

  return (
    <>
      {textareaStyle && currentTextOverlayForEditing && (
        <style
          key={`textarea-style-${currentTextOverlayForEditing.position.x.toFixed(
            2
          )}-${currentTextOverlayForEditing.position.y.toFixed(2)}-${
            currentTextOverlayForEditing.fontSize
          }-${currentTextOverlayForEditing.rotation || 0}`}
        >
          {`.${styles.videoItemPlayer} .${
            styles.playerRatio
          } > textarea.inline-text-edit-textarea {
            width: ${textareaStyle.width} !important;
            height: ${textareaStyle.height} !important;
            top: ${textareaStyle.top} !important;
            left: ${textareaStyle.left} !important;
            right: auto !important;
            bottom: auto !important;
            overflow: hidden !important;
            scroll-behavior: auto !important;
            // inset: auto !important;
            transform: translate(-50%, -50%) rotate(${
              currentTextOverlayForEditing?.rotation || 0
            }deg) !important;
          }`}
        </style>
      )}
      <canvas
        ref={canvasRef}
        data-overlay-canvas={true}
        onContextMenu={(e) => e.preventDefault()}
        onDoubleClick={readOnlyMode ? undefined : handleDoubleClick}
        onClick={(e) => {
          if (
            onCloseLogoSliders &&
            ((externalShowTextModal && externalTextEditingState?.isEditing) ||
              (externalShowLogoModal && externalLogoEditingState?.isEditing))
          ) {
            onCloseLogoSliders();
          }
        }}
        onMouseDown={(e) => {
          if (!showCanvasOverlay || readOnlyMode) {
            return;
          }
          handleMouseDown(e);
        }}
        onMouseMove={(e) => {
          if (!showCanvasOverlay || readOnlyMode) {
            return;
          }
          handleCursorMove(e);
        }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: videoDimensions?.width,
          height: videoDimensions?.height,
          pointerEvents: showCanvasOverlay && !readOnlyMode ? "auto" : "none",
          zIndex: 10,
          objectFit: "contain",
          cursor: readOnlyMode
            ? "default"
            : dragState.isDragging
            ? "grabbing"
            : showCanvasOverlay
            ? "grab"
            : "default",
        }}
      />
      {inlineTextEditingState.isEditing &&
        !readOnlyMode &&
        (() => {
          const currentTextOverlay = textOverlays.find(
            (overlay) => overlay.id === inlineTextEditingState.textOverlayId
          );
          const opacity = currentTextOverlay?.opacity ?? 1.0;
          if (opacity <= 0.01) {
            return null;
          }

          return (
            <textarea
              ref={inlineInputRef}
              key={`inline-edit-${inlineTextEditingState.textOverlayId}`}
              value={inlineTextEditingState.text ?? ""}
              placeholder="New Text"
              onChange={(e) => {
                if (inlineTextEditingState.textOverlayId) {
                  const textarea = e.target as HTMLTextAreaElement;
                  const caretPosition = textarea.selectionStart;
                  const caretEnd = textarea.selectionEnd;
                  const currentOverlay = textOverlays.find(
                    (overlay) =>
                      overlay.id === inlineTextEditingState.textOverlayId
                  );
                  let newText = e.target.value;
                  if (
                    currentOverlay?.text === "New Text" &&
                    newText.length > 0 &&
                    newText !== "New Text"
                  ) {
                    if (newText.startsWith("New Text")) {
                      newText = newText.replace(/^New Text\s*/, "");
                    }
                  }
                  if (newText.length === 0) {
                    newText = "";
                  }

                  updateTextOverlay(
                    inlineTextEditingState.textOverlayId,
                    {
                      text: newText,
                    },
                    false
                  );

                  setInlineTextEditingState((prev) => ({
                    ...prev,
                    text: newText,
                  }));
                  setTimeout(() => {
                    if (inlineInputRef.current) {
                      inlineInputRef.current.setSelectionRange(
                        caretPosition,
                        caretEnd
                      );
                      // Reset scroll position after text changes to prevent scrolling
                      inlineInputRef.current.scrollTop = 0;
                      inlineInputRef.current.scrollLeft = 0;
                    }
                  }, 0);
                } else {
                  setInlineTextEditingState((prev) => ({
                    ...prev,
                    text: e.target.value,
                  }));
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  const textarea = e.target as HTMLTextAreaElement;
                  const finalText = textarea.value || "";
                  if (inlineTextEditingState.textOverlayId) {
                    updateTextOverlay(
                      inlineTextEditingState.textOverlayId,
                      {
                        text: finalText,
                      },
                      true
                    );
                  }
                  setInlineTextEditingState(resetInlineTextEditingState());
                } else if (e.key === "Enter") {
                  // Reset scroll position when Enter is pressed to prevent scrolling
                  setTimeout(() => {
                    if (inlineInputRef.current) {
                      inlineInputRef.current.scrollTop = 0;
                      inlineInputRef.current.scrollLeft = 0;
                    }
                  }, 0);
                }
              }}
              onBlur={(e) => {
                const textarea = e.target as HTMLTextAreaElement;
                const finalText = textarea.value || "";
                if (inlineTextEditingState.textOverlayId) {
                  updateTextOverlay(
                    inlineTextEditingState.textOverlayId,
                    {
                      text: finalText,
                    },
                    true
                  );
                }
                setInlineTextEditingState((prev) => ({
                  ...resetInlineTextEditingState(),
                  text: finalText,
                }));
              }}
              style={{
                position: "absolute",
                fontFamily:
                  inlineTextEditingState.fontFamily || "Arial, sans-serif",
                fontWeight: inlineTextEditingState.isBold ? "bold" : "normal",
                fontStyle: inlineTextEditingState.isItalic
                  ? "italic"
                  : "normal",
                textDecoration: inlineTextEditingState.isUnderline
                  ? "underline"
                  : "none",
                color: "transparent",
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                resize: "none",
                padding: `${inlineTextEditingState.padding}px`,
                lineHeight: "1.2",
                textAlign: "center",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                overflow: "hidden",
                zIndex: 1000,
                pointerEvents: "auto",
                transformOrigin: "center",
                caretColor: "#ffffff",
                fontSize: `${inlineTextEditingState.fontSize}px`,
                scrollBehavior: "auto",
              }}
              className="inline-text-edit-textarea"
              autoFocus
            />
          );
        })()}
    </>
  );
};

export default CanvasOverlayRenderer;
